import { fetchPost } from './fetchHelper'

interface Count {
  count: number
}

export interface FinancialCalcReq {
  target: number // 目標額
  initial: number // 初期投資額
  addition: number // 月次投資額
  interest: number // 年率利回り
  depositInterest: number // 預金の年率金利
  months: number // 投資月数
  resType: string // 計算対象文字列
}

export interface FinancialCalcRes {
  statusCode: number
  result?: FinancialCalcResult
}

export interface FinancialCalcResult {
  principal: number
  amount: number
  depositAmount: number
  addition: number
  depositAddition: number
  interest: number
  months: number
  depositMonths: number
  monthList: number[]
  principalList: number[]
  profitList: number[]
  amountList: number[]
}

export interface SimulationReq {
  initial: number // 初期投資額
  addition: number // 月次投資額
  ret: number // 年率利回り（％）
  risk: number // 年率リスク（％）
  additionRet: number // 月次投資額の年率利回り（％）
  additionRisk: number // 月次投資額の年率リスク（％）
  depositRet: number // 預金の年率金利（％）
  months: number // 投資月数
}

export interface SimulationRes {
  statusCode: number
  result?: SimulationResult
}

export interface SimulationResult {
  veryGood: SimulationCaseResult
  good: SimulationCaseResult
  littleGood: SimulationCaseResult
  median: SimulationCaseResult
  littleBad: SimulationCaseResult
  bad: SimulationCaseResult
  veryBad: SimulationCaseResult
  principal: number
  depositAmount: number
}

export interface SimulationCaseResult {
  sample: number
  profit?: number
}

export interface CalcReversalReq {
  initial: number
  addition: number
  interest1: number
  interest2: number
  startAge: number
  turningAddition?: number
  turningAge?: number
}

export interface CalcReversalRes {
  statusCode: number
  result?: CalcReversalResult
}

export interface CalcReversalResult {
  depositMonths?: number
  months1?: number
  months2?: number
  interest1: number
  interest2: number
  startAge: number
  turningAge?: number
  depositList: number[]
  amountList1: number[]
  amountList2: number[]
}

export interface FinancialCalcReversalReq {
  initial: number // 取崩開始時点の残高
  startAge: number // 取崩開始年齢
  interest: number // 年率利回り（％）
  isMonthlyWithdrawal: boolean // 毎月取り崩しかどうか。true:毎月取り崩し, false:隔月取り崩し
  disSavingRate: number | null // 定率取崩の"年次"取り崩し率（％）
  addition: number | null // 定額取崩の月次取り崩し率
}

export interface FinancialCalcReversalRes {
  statusCode: number
  result?: FinancialCalcReversalResult
}

export interface FinancialCalcReversalResult {
  interest: number // 入力の年率利回り
  startAge: number // 入力の取り崩しを始める年齢
  // 定額取り崩しの結果
  fixed?: {
    depositMonths: number | null // 運用無しでの残高が0になるまでの経過月数。nullは0円にならなかったときになる。
    investmentMonths: number | null // 運用ありでの残高が0になるまでの経過月数。nullは0円にならなかったときになる。
    depositList: Array<{
      amount: number // 残高
      months: number // 取り崩ししてきた月数（その年を上限とし、取り崩しできなくなった場合増えない）
    }>
    investmentList: Array<{
      amount: number // 残高
      months: number // 取り崩ししてきた月数（その年を上限とし、取り崩しできなくなった場合増えない）
    }>
  }
  // 定率取り崩しの結果
  rate?: {
    depositMonths?: number // 運用無しでの残高が1以下になるまでの経過月数。条件に満たすときのみ存在。
    investmentMonths?: number // 運用ありでの残高が1以下になるまでの経過月数。条件に満たすときのみ存在。
    // 運用無しでの各年の結果
    depositList: Array<{
      amount: number // 残高
      addition: number // その年の最終月の月次取崩額 or その年の取崩総額（仕様による）
      months: number // 取り崩ししてきた月数（その年を上限とし、取り崩しできなくなった場合増えない）
    }>
    // 運用アリでの各年の結果
    investmentList: Array<{
      amount: number // 残高
      addition: number // その年の最終月の月次取崩額 or その年の取崩総額（仕様による）
      months: number // 取り崩ししてきた月数（その年を上限とし、取り崩しできなくなった場合増えない）
    }>
  }
}

export interface FrontAccessLog {
  current: string
}

export async function apiIncrement(request: Count) {
  return await fetchPost<Count>('/api/increment', request)
}

export async function apiDecrement(request: Count) {
  return await fetchPost<Count>('/api/decrement', request)
}

export async function apiFinancialCalc(request: FinancialCalcReq) {
  return await fetchPost<FinancialCalcRes>('/api/financialCalc', request)
}

export async function apiSimulation(request: SimulationReq) {
  return await fetchPost<SimulationRes>('/api/simulation', request)
}

export async function apiCalcReversal(request: CalcReversalReq) {
  return await fetchPost<CalcReversalRes>('/api/calcReversal', request)
}

export async function apiFinancialCalcReversal(request: FinancialCalcReversalReq) {
  return await fetchPost<FinancialCalcReversalRes>('/api/financialCalcReversal', request)
}

export async function apiFrontAccessLog(request: FrontAccessLog) {
  return await fetchPost('/api/access', request)
}
